import { useState } from "react";
import { api, formatApiError } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import AddressAutocomplete from "@/components/AddressAutocomplete";

export function AddUserDialog({ role, onCreated, label }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const isCustomer = role === "customer";
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", address: "", address_line2: "", city: "" });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) { toast.error("Name and phone are required"); return; }
    setBusy(true);
    try {
      if (isCustomer) {
        // Lightweight customer creation: no password (OTP/Google login). Captures address.
        await api.post("/admin/customers", {
          name: form.name,
          phone: form.phone,
          email: form.email || null,
          address: form.address,
          address_line2: form.address_line2,
          city: form.city,
        });
      } else {
        if (form.password.length < 6) { toast.error("Password must be at least 6 characters"); setBusy(false); return; }
        // Chef email is optional — derive a placeholder from phone if blank.
        const email = form.email && form.email.trim() ? form.email.trim() : `${form.phone.replace(/\D/g, "")}@chef.delcochefs.local`;
        await api.post("/admin/users", { ...form, email, role });
      }
      toast.success(`${isCustomer ? "Customer" : "Chef"} added`);
      setForm({ name: "", email: "", phone: "", password: "", address: "", address_line2: "", city: "" });
      setOpen(false);
      onCreated?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="rounded-full bg-primary hover:bg-primary/90" data-testid={`add-${role}-btn`}>
          <Plus size={16} className="mr-1"/> {label}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid={`add-${role}-dialog`}>
        <DialogHeader>
          <DialogTitle className="heading">Add {isCustomer ? "Customer" : "Chef"}</DialogTitle>
          <DialogDescription>
            {isCustomer
              ? "Capture customer details. They sign in via OTP — no password needed."
              : "Basic details. They can complete the rest after first login."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 py-2">
          <div>
            <Label>Full name <span className="text-rose-500">*</span></Label>
            <Input required value={form.name} onChange={e=>setForm({...form, name:e.target.value})} data-testid={`new-${role}-name`} />
          </div>
          <div>
            <Label>Phone <span className="text-rose-500">*</span></Label>
            <Input
              required
              value={form.phone}
              onChange={e => setForm({...form, phone: e.target.value.replace(/[^\d+]/g, "").slice(0, 15)})}
              onKeyDown={e => { if (!/^[\d+]$/.test(e.key) && !["Backspace","Delete","Tab","ArrowLeft","ArrowRight","Home","End"].includes(e.key)) e.preventDefault(); }}
              placeholder="+91…"
              inputMode="tel"
              data-testid={`new-${role}-phone`}
            />
          </div>
          <div>
            <Label>Email <span className="text-xs text-muted-foreground">(optional)</span></Label>
            <Input
              type="text"
              value={form.email}
              onChange={e=>setForm({...form, email:e.target.value})}
              placeholder="Skip if not available"
              data-testid={`new-${role}-email`}
            />
          </div>
          {isCustomer ? (
            <>
              <div>
                <Label>Locality / area <span className="text-rose-500">*</span></Label>
                <AddressAutocomplete
                  value={form.address}
                  onChange={(v) => setForm({ ...form, address: v })}
                  onSelect={({ address }) => setForm({ ...form, address })}
                  placeholder="Start typing — Google suggests addresses"
                  multiline
                  data-testid={`new-${role}-address`}
                />
              </div>
              <div>
                <Label>Flat / floor / building <span className="text-xs text-muted-foreground">(exact address)</span></Label>
                <Input
                  value={form.address_line2}
                  onChange={e=>setForm({...form, address_line2: e.target.value})}
                  placeholder="Flat 12A, Tower B, Landmark near..."
                  data-testid={`new-${role}-address-line2`}
                />
              </div>
              <div>
                <Label>City</Label>
                <Input value={form.city} onChange={e=>setForm({...form, city:e.target.value})} placeholder="Noida, UP 201301" data-testid={`new-${role}-city`} />
              </div>
            </>
          ) : (
            <div>
              <Label>Temporary password <span className="text-xs text-muted-foreground">(min 6 chars)</span></Label>
              <Input required type="text" value={form.password} onChange={e=>setForm({...form, password:e.target.value})} data-testid={`new-${role}-password`} />
            </div>
          )}
          <DialogFooter className="gap-2 pt-3">
            <Button type="button" variant="outline" className="rounded-full" onClick={()=>setOpen(false)}>Cancel</Button>
            <Button type="submit" disabled={busy} className="rounded-full bg-primary hover:bg-primary/90" data-testid={`new-${role}-submit`}>
              {busy ? "Adding..." : "Add"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function DeleteUserButton({ userId, userName, onDeleted }) {
  const [busy, setBusy] = useState(false);
  const remove = async () => {
    if (!window.confirm(`Permanently delete ${userName}? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/users/${userId}/hard`);
      toast.success("Deleted");
      onDeleted?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <Button size="sm" variant="ghost" onClick={remove} disabled={busy} className="text-destructive hover:text-destructive hover:bg-destructive/10" data-testid={`delete-${userId}`}>
      <Trash2 size={14}/>
    </Button>
  );
}

/**
 * Inline bulk-delete toolbar used inside any admin user table.
 * Renders a "Select all / Delete N" pill row above the table.
 * Pass the parent's `selected`/`onChange` state plus the user list.
 */
export function BulkDeleteBar({ users, selected, setSelected, onDeleted, label = "users" }) {
  const selectedIds = Object.keys(selected).filter(k => selected[k]);
  const allOn = users.length > 0 && selectedIds.length === users.length;
  const toggleAll = () => {
    if (allOn) setSelected({});
    else setSelected(Object.fromEntries(users.map(u => [u.id, true])));
  };
  const bulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Delete ${selectedIds.length} ${label.replace(/s$/, "")}${selectedIds.length === 1 ? "" : "s"}? This cannot be undone.`)) return;
    try {
      const { data } = await api.post("/admin/users/bulk-delete", { ids: selectedIds });
      toast.success(`Deleted ${data.deleted} ${label}`);
      setSelected({});
      onDeleted?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Bulk delete failed"); }
  };
  return (
    <div className="flex items-center gap-2 mb-3">
      <Button size="sm" variant="outline" className="rounded-full text-xs" onClick={toggleAll} disabled={users.length === 0} data-testid={`bulk-toggle-all-${label}`}>
        {allOn ? "Clear selection" : "Select all"}
      </Button>
      {selectedIds.length > 0 && (
        <Button size="sm" className="rounded-full bg-rose-600 hover:bg-rose-700 text-white text-xs" onClick={bulkDelete} data-testid={`bulk-delete-${label}`}>
          <Trash2 size={12} className="mr-1.5"/> Delete {selectedIds.length} {label}
        </Button>
      )}
    </div>
  );
}

export function SelectCheckbox({ id, selected, setSelected, testid }) {
  const on = !!selected[id];
  return (
    <input
      type="checkbox"
      checked={on}
      onChange={() => setSelected(s => ({ ...s, [id]: !on }))}
      className="w-4 h-4 rounded border-border accent-primary cursor-pointer"
      data-testid={testid || `select-${id}`}
    />
  );
}
